import React from "react";
import InputField from "../../SharedComponent/Fields/InputField";
import SelectField from "../../SharedComponent/Fields/SelectField";
import TextAreaField from "../../SharedComponent/Fields/TextareaField";
import { ImagePlus, Upload, X } from "lucide-react";
import { getAttachmentLargeUrl, getAttachmentThumbUrl, getAttachmentDocumentUrl } from "../../utils/attachmentImages";
import { useTranslation } from "react-i18next";

// Shared engine type options
const engineTypeOptions = [
  { value: "Diesel", option: "Diesel" },
  { value: "Electric", option: "Electric" },
  { value: "LPG", option: "LPG" },
  { value: "Gasoline", option: "Gasoline/Gas" },
  { value: "Hybrid", option: "Hybrid" },
  { value: "Fuel-Cell", option: "Fuel-Cell" },
  { value: "Li-Ion", option: "Lithium-Ion" },
  { value: "manual/none", option: "Manual/None" },
];

const CleaningEquipment = ({
  formatAttachment,
  handleDeleteAttachment,
  assetAttachments,
  newAsset,
  handleChange,
  warehouses,
  selectedWarehouse,
  handleWarehouseChange,
  warehouseLocationId,
  selectedFiles,
  handleRemoveFile,
  handleFileChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Basics Section */}
      <div className="bg-white p-4 shadow-md">
        <h2 className="text-xl font-medium mb-3 text-gray-800">
          {t("assetPageHeadings.basics")}
        </h2>

        {/* Row 1: Classification, Manufacturer, Type, Serial No */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <SelectField
            name="cleaning_toc"
            value={newAsset.cleaning_toc}
            onChange={handleChange}
            required
            label={t("assets.fields.classification")}
            explanation={t("assets.fields.explanation.classification")}
            placeholder={t("assets.fields.selectClassification")}
            menus={[
              {
                value: "",
                option: t("cleaningEquipment.classificationTypes.select"),
              },
              {
                value: 1,
                option: t("cleaningEquipment.classificationTypes.other"),
              },
              {
                value: 2,
                option: t(
                  "cleaningEquipment.classificationTypes.pedestrianSweeper"
                ),
              },
              {
                value: 3,
                option: t(
                  "cleaningEquipment.classificationTypes.pedestrianVacuumSweeper"
                ),
              },
              {
                value: 4,
                option: t(
                  "cleaningEquipment.classificationTypes.pedestrianScrubberDryer"
                ),
              },
              {
                value: 5,
                option: t(
                  "cleaningEquipment.classificationTypes.streetCleaningMachine"
                ),
              },
              {
                value: 6,
                option: t(
                  "cleaningEquipment.classificationTypes.pressureWasher"
                ),
              },
              {
                value: 7,
                option: t(
                  "cleaningEquipment.classificationTypes.pedestrianScrubberDryer2"
                ),
              },
              {
                value: 8,
                option: t(
                  "cleaningEquipment.classificationTypes.rideOnScrubberDryer"
                ),
              },
              {
                value: 9,
                option: t(
                  "cleaningEquipment.classificationTypes.rideOnSweeper"
                ),
              },
              {
                value: 10,
                option: t(
                  "cleaningEquipment.classificationTypes.rideOnVacuumSweeper"
                ),
              },
              {
                value: 11,
                option: t("cleaningEquipment.classificationTypes.steamMachine"),
              },
              {
                value: 12,
                option: t(
                  "cleaningEquipment.classificationTypes.vacuumCleaner"
                ),
              },
              {
                value: 13,
                option: t(
                  "cleaningEquipment.classificationTypes.escalatorCleaner"
                ),
              },
              {
                value: 14,
                option: t(
                  "cleaningEquipment.classificationTypes.singleDiscMachine"
                ),
              },
              {
                value: 15,
                option: t(
                  "cleaningEquipment.classificationTypes.carpetCleaner"
                ),
              },
              {
                value: 16,
                option: t(
                  "cleaningEquipment.classificationTypes.ridingScrubberDryer"
                ),
              },
            ]}
            selectClassName={
    !newAsset.cleaning_toc
      ? "text-gray-400 font-light"
      : "text-gray-900 font-normal"
  }
          />

          <InputField
            name="manufacturer"
            value={newAsset.manufacturer}
            required
            onChange={handleChange}
            label={t("assets.fields.manufacturer")}
            explanation={t("assets.fields.explanation.manufacturer")}
            placeholder={t("createAsset.placeholder.enterManufacturer")}
            maxLength={100}
          />

          <InputField
            name="type"
            value={newAsset.type}
            required
            onChange={handleChange}
            label={t("assets.fields.type")}
            placeholder={t("createAsset.placeholder.enterType")}
            explanation={t("assets.fields.explanation.type")}
            maxLength={100}
          />

          <InputField
            name="serial_no"
            value={newAsset.serial_no}
            onChange={handleChange}
            placeholder={t("createAsset.placeholder.enterSerialNumber")}
            explanation={t("assets.fields.serialNumber")}
            label={t("assets.fields.machineSerialNo")}
            maxLength={100}
          />
        </div>

        {/* Row 2: Internal No, Status, Engine Type, Year */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          <InputField
            name="cleaning_internalno"
            value={newAsset.cleaning_internalno}
            onChange={handleChange}
            label={t("createAsset.placeholders.internNoLabel")}
            explanation={t("createAsset.placeholders.internNoExplanation")}
            placeholder={t("createAsset.placeholders.internNoPlaceholder")}
            maxLength={100}
          />

          <SelectField
            name="status"
            value={newAsset.status}
            onChange={handleChange}
            label={t("assets.fields.status")}
            placeholder={t("createAsset.placeholder.enterStatus")}
            explanation={t("assets.fields.explanation.status")}
            menus={[
              { value: "", option: t("assets.fields.selectStatus") },
              { value: "New", option: t("assets.options.new") },
              { value: "Used", option: t("assets.options.used") },
              { value: "Demo", option: t("assets.options.demo") },
            ]}
          />

          <SelectField
            name="cleaning_enginetype"
            value={newAsset.cleaning_enginetype}
            onChange={handleChange}
            label={t("createAsset.placeholders.engineTypeLabel")}
            explanation={t("createAsset.placeholders.engineTypeExplanation")}
            placeholder={t("createAsset.placeholders.engineTypePlaceholder")}
            menus={[
              { value: "", option: t("workPlatform.placeholders.engineType") },
              { value: 1, option: t("assets.options.diesel") },
              { value: 2, option: t("assets.options.electric") },
              { value: 3, option: t("assets.options.lpg") },
              { value: 4, option: t("assets.options.gasoline-gas") },
              { value: 5, option: t("assets.options.hybrid") },
              { value: 6, option: t("assets.options.fuel-cell") },
              { value: 7, option: t("assets.options.lithium-ion") },
              { value: 8, option: t("assets.options.hand") },
            ]}
          />

          <InputField
            name="year_of_manufacture"
            value={newAsset.year_of_manufacture || ""}
            onChange={handleChange}
            label={t("assets.fields.yearOfManufacture")}
            explanation={t("assets.fields.explanation.yearOfManufacture")}
            placeholder={t("createAsset.placeholder.enterYearOfManufacture")}
            type="year"
          />
          <div>
            <InputField
              name="operating_hours"
              type="number"
              value={newAsset.operating_hours}
              onChange={handleChange}
              label={t("assets.fields.operatingHours")}
              explanation={t("assets.fields.explanation.operatingHours")}
              placeholder={t("cleaningEquipment.placeholders.operatingHours")}
              maxLength={8}
            />
          </div>
        </div>

        {/* Row 3: Operating Hours, Sweeping Width, Suction Width, Working Width */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          <InputField
            name="cleaning_sweeping_width"
            type="number"
            value={newAsset.cleaning_sweeping_width}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.sweepingWidth")}
            explanation={t("cleaningEquipment.explanations.sweepingWidth")}
            placeholder={t("cleaningEquipment.placeholders.sweepingWidth")}
          />
          <InputField
            name="cleaning_suctionwidth"
            type="number"
            value={newAsset.cleaning_suctionwidth}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.suctionWidth")}
            explanation={t("cleaningEquipment.explanations.suctionWidth")}
            placeholder={t("cleaningEquipment.placeholders.suctionWidth")}
          />

          <InputField
            name="cleaning_workingwidth"
            type="number"
            value={newAsset.cleaning_workingwidth}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.workingWidth")}
            explanation={t("cleaningEquipment.explanations.workingWidth")}
            placeholder={t("cleaningEquipment.placeholders.workingWidth")}
          />
          <InputField
            name="cleaning_brush_pressure"
            type="number"
            value={newAsset.cleaning_brush_pressure}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.brushPressure")}
            explanation={t("cleaningEquipment.explanations.brushPressure")}
            placeholder={t("cleaningEquipment.placeholders.brushPressure")}
          />

          <InputField
            name="cleaning_area_performance"
            type="number"
            value={newAsset.cleaning_area_performance}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.areaPerformance")}
            explanation={t("cleaningEquipment.explanations.areaPerformance")}
            placeholder={t("cleaningEquipment.placeholders.areaPerformance")}
          />
        </div>

        {/* Row 4: Brush Pressure, Area Performance, Water Tanks, Container */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2">
          <InputField
            name="cleaning_fresh_tank"
            type="number"
            value={newAsset.cleaning_fresh_tank}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.freshTank")}
            explanation={t("cleaningEquipment.explanations.freshTank")}
            placeholder={t("cleaningEquipment.placeholders.freshTank")}
          />

          <InputField
            name="cleaning_dirty_tank"
            type="number"
            value={newAsset.cleaning_dirty_tank}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.dirtyTank")}
            explanation={t("cleaningEquipment.explanations.dirtyTank")}
            placeholder={t("cleaningEquipment.placeholders.dirtyTank")}
          />
          <InputField
            name="cleaning_container"
            type="number"
            value={newAsset.cleaning_container}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.container")}
            explanation={t("cleaningEquipment.explanations.container")}
            placeholder={t("cleaningEquipment.placeholders.container")}
          />

          <InputField
            name="cleaning_weight"
            type="number"
            value={newAsset.cleaning_weight}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.weight")}
            explanation={t("cleaningEquipment.explanations.weight")}
            placeholder={t("cleaningEquipment.placeholders.weight")}
          />
        </div>

        {/* Row 5: Container Volume, Weight, Dimensions */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          <InputField
            name="cleaning_length"
            type="number"
            value={newAsset.cleaning_length}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.length")}
            explanation={t("cleaningEquipment.explanations.length")}
            placeholder={t("cleaningEquipment.placeholders.length")}
          />

          <InputField
            name="cleaning_width"
            type="number"
            value={newAsset.cleaning_width}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.width")}
            explanation={t("cleaningEquipment.explanations.width")}
            placeholder={t("cleaningEquipment.placeholders.width")}
          />

          <InputField
            name="cleaning_height"
            type="number"
            value={newAsset.cleaning_height}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.height")}
            explanation={t("cleaningEquipment.explanations.height")}
            placeholder={t("cleaningEquipment.placeholders.height")}
          />
          <InputField
            name="commission"
            value={newAsset.commission}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.commission")}
            explanation={t("cleaningEquipment.explanations.commission")}
            placeholder={t("cleaningEquipment.placeholders.commission")}
            type="text"
          />

          <InputField
            name="cleaning_available"
            type="date"
            value={newAsset.cleaning_available}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.availableFrom")}
            explanation={t("cleaningEquipment.explanations.availableFrom")}
            placeholder={t("cleaningEquipment.placeholders.availableFrom")}
          />
        </div>

        {/* Row 6: Condition, Battery, Commission, Available From */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
          {/* <div>
            <InputField
              required
              name="year_of_manufacture"
              value={newAsset.year_of_manufacture || ""}
              onChange={handleChange}
              label={t("assets.fields.yearOfManufacture")}
              explanation={t("assets.fields.explanation.yearOfManufacture")}
              placeholder={t("createAsset.placeholder.enterYearOfManufacture")}
              type="month"
              maxLength={100}
            />
          </div> */}
          <SelectField
            name="technical_condition"
            value={newAsset.technical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.technicalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.technicalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.technicalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.technicalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.technicalCondition.very_good"),
              },
              {
                value: 3,
                option: t("assets.technicalCondition.good"),
              },
              {
                value: 4,
                option: t("assets.technicalCondition.used"),
              },
              {
                value: 5,
                option: t("assets.technicalCondition.bad"),
              },
              {
                value: 6,
                option: t("assets.technicalCondition.very_bad"),
              },
              {
                value: 7,
                option: t("assets.technicalCondition.new"),
              },
            ]}
          />
          <SelectField
            name="optical_condition"
            value={newAsset.optical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.opticalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.opticalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.opticalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.opticalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.opticalCondition.very_good"),
              },
              { value: 3, option: t("assets.opticalCondition.good") },
              { value: 4, option: t("assets.opticalCondition.used") },
              { value: 5, option: t("assets.opticalCondition.bad") },
              { value: 6, option: t("assets.opticalCondition.very_bad") },
              { value: 7, option: t("assets.opticalCondition.new") },
            ]}
          />

          <InputField
            name="cleaning_battery"
            value={newAsset.cleaning_battery}
            onChange={handleChange}
            label={t("cleaningEquipment.fields.battery")}
            explanation={t("cleaningEquipment.explanations.battery")}
            placeholder={t("cleaningEquipment.placeholders.battery")}
            maxLength={100}
          />
        </div>

        {/* Row 7: Warehouse + Address */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          {Array.isArray(warehouses) && warehouses?.length > 0 && (
            <SelectField
              name="warehouse"
              value={selectedWarehouse?.id || warehouseLocationId || ""}
              onChange={handleWarehouseChange}
              label="Warehouse"
              menus={[
                { value: "", option: "Select Warehouse" },
                ...warehouses.map((w) => ({ value: w.id, option: w.name })),
              ]}
            />
          )}

          <div>
            <InputField
              required
              name="dealer_street"
              value={newAsset.dealer_street}
              onChange={handleChange}
              maxLength={20}
              label={t("createAsset.placeholders.street")}
              placeholder={t("createAsset.placeholders.streetPlaceholder")}
              explanation={t("createAsset.placeholders.streetExplanation")}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_city"
              value={newAsset.dealer_city}
              onChange={handleChange}
              label={t("createAsset.placeholders.city")}
              explanation={t("createAsset.placeholders.cityExplanation")}
              placeholder={t("createAsset.placeholders.cityPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_country"
              value={newAsset.dealer_country}
              onChange={handleChange}
              label={t("createAsset.placeholders.country")}
              explanation={t("createAsset.placeholders.countryExplanation")}
              placeholder={t("createAsset.placeholders.countryPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_zip"
              value={newAsset.dealer_zip}
              onChange={handleChange}
              type="number"
              label={t("createAsset.placeholders.zipCode")}
              explanation={t("createAsset.placeholders.zipCodePlaceholder")}
              placeholder={t("createAsset.placeholders.zipCodeExplanation")}
              maxLength={7}
            />
          </div>
        </div>

        {/* Description */}
        <div className="mt-2">
          <TextAreaField
            name="description"
            value={newAsset.description}
            onChange={handleChange}
            label={t("createAsset.placeholders.additionalDescription")}
            placeholder={t(
              "createAsset.placeholders.additionalDescriptionPlaceholder"
            )}
          />
        </div>
      </div>

      {/* Media Upload Section */}
      <div className="bg-white p-4 shadow-md mt-4">
        <h2 className="text-xl font-medium mb-3 text-gray-800">Media</h2>

        {/* Selected Files */}
        {selectedFiles?.length > 0 && (
          <div className="space-y-2 mt-2">
            <h6>Selected Media</h6>
            <ul className="space-y-1 flex flex-col gap-3">
              {Array.from(selectedFiles).map((file, index) => (
                <li key={index} className="flex items-center gap-4">
                  {file.type.startsWith("image/") && (
                    <img
                      className="h-10 w-10"
                      alt={`cleaning ${index}`}
                      src={URL.createObjectURL(file)}
                    />
                  )}
                  <a
                    href={URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-sm font-semibold underline ml-2"
                  >
                    {file.name}
                  </a>
                  <div
                    onClick={() => handleRemoveFile(index)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Uploaded Media */}
        {assetAttachments?.length > 0 && (
          <div className="flex flex-col gap-2 mt-4">
            <h6>Uploaded Media</h6>
            <div className="flex flex-col gap-3">
              {assetAttachments.map((attachment, index) => (
                <div key={index} className="flex items-center gap-2">
                  {attachment?.image ? (
                    <>
                      <img
                        className="h-10 w-10"
                        src={getAttachmentThumbUrl(attachment)}
                        alt="cleaning equipment"
                      />
                      <a
                        href={getAttachmentLargeUrl(attachment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm font-semibold underline ml-2"
                      >
                        {formatAttachment(getAttachmentLargeUrl(attachment))}
                      </a>
                    </>
                  ) : (
                    <a
                      href={getAttachmentDocumentUrl(attachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm font-semibold underline ml-2"
                    >
                      {formatAttachment(getAttachmentDocumentUrl(attachment))}
                    </a>
                  )}
                  <div
                    onClick={() => handleDeleteAttachment(attachment?.id)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Upload */}
        <div className="my-4">
          <div className="relative">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              accept="image/*, .xlsx, .xls, .doc, .docx, .pdf"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              id="cleaning-upload"
            />
            <label
              htmlFor="cleaning-upload"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-gray-400"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <ImagePlus className="w-6 h-6 text-gray-400" />
                </div>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">
                    {t("assetPageHeadings.clickToUpload")}
                  </span>
                </p>
                <p className="text-xs text-gray-400">
                  {t("assetPageHeadings.fileSupported")}
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default CleaningEquipment;
